import { Injectable } from '@angular/core';
import { TAppMaster } from '@monorepo/resolvers';
import { Store, select } from '@ngrx/store';

import {
  CORRECTIVE_ACTIONS_LIST_SYNCING_KEY,
  TAppSyncScreenItem,
  TBctxItemWithoutSize,
  TMappedIssueDTO,
  TGenericIssueDTO,
  TMasterdataListResult,
  TMasterdataRejectList,
  TOperationAnsIssueAndSilhouetteImagesResult,
  TOperationAnsIssueAndSilhouetteImagesResultSuccess,
  TRecentOperationAndIssue,
  TREQUEST_masterdataListsV2,
  TSilhouetteKeyResult,
  TSilhouetteOperationOutput,
  TOperationsAndIssuesAndSilhouetteOperations,
  TUpdateFilteredListsV3,
  TDepartmentWiseOperationAndGenericIssueResult,
  TMasterDataListMetaData,
} from '@monorepo/types';
import { Observable, combineLatest, Subject } from 'rxjs';
import { map, take } from 'rxjs/operators';
import { BCTXComputationHelper, InputAppAPIHelperUtil, MasterdataUtil } from '@monorepo/utils';
import {
  AddOrUpdateMasterdataListByName,
  DownloadSilhouetteAssets,
  InitializeMasterdataListStore,
  LoadAllMasterdataLists,
  LoadOperationsAndIssuesV2,
  LoadCorrectiveReasons,
  LoadSilhouetteInfo,
  UpdateFilteredLists,
  UpdateFilteredRejectList,
  UpdateFilteredListsV3,
  LoadRCAHierarchicalData,
} from './master-data-list-db.actions';
import {
  getDownloadSilhouetteAssetsPayload,
  getLoadCorrectiveReasonsPayload,
  getCorrectiveReasonList,
  isCorrectiveReasonsAvailable,
  getSilhouetteKeysInfoAndBctxOrderKeysAndConf,
  getAllMasterdataListsInfoAndBctxOrderKeysAndConf,
  selectMasterdataListStore,
  getAllMasterdataListsInfo,
  rcaHierarchicalData,
} from './master-data-list-db.query';
import { InputAppClientDBFacade } from '../input-app-client-db/input-app-client-db.facade';
import { AppMasterDBFacade } from '../app-master-db/app-master-db.facade';
import { getBctxItemsOrderWithoutSize } from '../app-master-db/app-master.query';
import produce from 'immer';
import { TranslateService } from '@ngx-translate/core';
@Injectable({
  providedIn: 'root',
})
export class MasterdataListDBFacade {
  private filteredListsUpdateSubscription = new Subject<void>();
  private masterDataListLoaded = new Subject<void>();

  private masterdataListLoadedsSubscription = new Subject<TMasterDataListMetaData>();
  constructor(
    private store: Store<TAppMaster>,
    private inputAppClientDBFacade: InputAppClientDBFacade,
    private appMasterFacade: AppMasterDBFacade,
    private inputAppHelperUtil: InputAppAPIHelperUtil,
    private translate: TranslateService,
  ) {}

  emitFilteredListsUpdate() {
    this.filteredListsUpdateSubscription.next();
  }

  getFilteredListsSubscription() {
    return this.filteredListsUpdateSubscription.asObservable();
  }

  updateMasterDataListLoaded() {
    this.masterDataListLoaded.next();
  }

  getMasterDataListLoadedStatus() {
    return this.masterDataListLoaded.asObservable();
  }

  filterRecentDefects(
    recentDefects: TRecentOperationAndIssue[],
    data: TOperationAnsIssueAndSilhouetteImagesResultSuccess,
  ) {
    const operationIdToMappedIssues: Record<string, Record<string, TMappedIssueDTO>> = {};
    data.operations.forEach((item) => {
      operationIdToMappedIssues[item.id] = item.issues.reduce((acc, curr) => {
        acc[curr.issueId] = curr;
        return acc;
      }, {} as Record<string, TMappedIssueDTO>);
    });
    const genericIssueToDTO = data.genericIssues.reduce((acc, curr) => {
      acc[curr.issueId] = curr;
      return acc;
    }, {} as Record<string, TGenericIssueDTO>);
    const validOperations = new Set<string>();
    data.silhouetteOperations.backImageOperations.forEach((item) => {
      item.operationIds.forEach((id) => validOperations.add(id));
    });
    data.silhouetteOperations.frontImageOperations.forEach((item) => {
      item.operationIds.forEach((id) => validOperations.add(id));
    });
    return recentDefects.filter((recentDefect) => {
      const operationId = recentDefect.operation.category_id;
      const issueId = recentDefect.issue.category_id;
      return (
        validOperations.has(operationId) &&
        (genericIssueToDTO[issueId] ||
          (operationIdToMappedIssues[operationId] && operationIdToMappedIssues[operationId][issueId]))
      );
    });
  }

  initializeMasterdataListStore(
    chainWithInitClientDB: boolean,
    chainWithInitCpiDB?: boolean,
    chainWithLoadRCAHierarchicalData?: boolean,
  ) {
    this.store.dispatch(
      new InitializeMasterdataListStore(chainWithInitClientDB, chainWithInitCpiDB, chainWithLoadRCAHierarchicalData),
    );
  }
  emitMasterdataListLoadedsSubscription(masterDataListMetaData: TMasterDataListMetaData = { source: 'UNKNOWN' }) {
    this.masterdataListLoadedsSubscription.next(masterDataListMetaData);
  }

  getMasterdataListLoadedsSubscription() {
    return this.masterdataListLoadedsSubscription.asObservable();
  }

  loadSilhouetteInfo() {
    this.inputAppClientDBFacade
      .getAllBctxItemsAcrossAllLinesAndMasterdataListConfig()
      .pipe(take(1))
      .subscribe(({ bctxAcrossAllLines, subjectKey, silhouetteConf }) => {
        if (bctxAcrossAllLines.length > 0) {
          this.store.dispatch(new LoadSilhouetteInfo(bctxAcrossAllLines, subjectKey, silhouetteConf));
        } else
          this.inputAppHelperUtil.showPopup(
            this.translate.instant('error_modal.Error'),
            this.translate.instant('input_app_master_data_facade.noBctxAvailableToFetchSilhouette'),
          );
      });
  }

  LoadRCAHierarchicalData(init: boolean, factoryId: string) {
    this.store.dispatch(new LoadRCAHierarchicalData(init, factoryId));
  }

  getRCAHierarchicalData() {
    return this.store.pipe(select(rcaHierarchicalData));
  }

  getCorrectiveActionsListKey() {
    return this.appMasterFacade.getAppSyncItemList().pipe(
      take(1),
      map((list: TAppSyncScreenItem[]) => {
        const item = list.find(
          (item) => item.type === 'ms_data' && item.id && item.id === CORRECTIVE_ACTIONS_LIST_SYNCING_KEY,
        );
        return item ? item.value : undefined;
      }),
    );
  }

  loadAllMasterdataListsAndCorrectiveActionsAndSilhouetteKeys(
    chainWithLoadSilhouetteInfo: boolean,
    masterDataListMetaData: TMasterDataListMetaData = { source: 'UNKNOWN' },
  ) {
    this.isCorrectiveReasonsAvailable()
      .pipe(take(1))
      .subscribe((res) => {
        if (res) {
          this.getCorrectiveActionsListKey()
            .pipe(take(1))
            .subscribe((res) => {
              if (res) {
                this.loadCorrectiveReasons(res, true, masterDataListMetaData);
              } else
                this.inputAppHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_facade.correctiveActionsNotConfigured.header'),
                  this.translate.instant('input_app_master_data_facade.correctiveActionsNotConfigured.message'),
                );
            });
        } else this.loadAllMasterdataLists(chainWithLoadSilhouetteInfo, masterDataListMetaData);
      });
  }

  loadOperationsAndIssuesV2(masterDataListMetaData: TMasterDataListMetaData = { source: 'UNKNOWN' }) {
    this.inputAppClientDBFacade
      .getAllBctxItemsAcrossAllLinesAndMasterdataListConfig()
      .pipe(take(1))
      .subscribe(
        ({
          bctxAcrossAllLines,
          silhouetteConf,
          masterdataList,
          listConf,
          subjectKey,
          operationsAndIssuesDepartmentId,
          operationsAndIssuesAllDepartmentIds,
        }) => {
          if (bctxAcrossAllLines.length > 0 && operationsAndIssuesDepartmentId) {
            const allOperationIssueDepartments = MasterdataUtil.getAllOperationIssueDepartments(
              operationsAndIssuesDepartmentId,
              operationsAndIssuesAllDepartmentIds,
            );

            this.store.dispatch(
              new LoadOperationsAndIssuesV2(
                false,
                false,
                bctxAcrossAllLines,
                subjectKey,
                masterdataList,
                allOperationIssueDepartments,
                listConf,
                silhouetteConf,
                masterDataListMetaData,
              ),
            );
          } else
            this.inputAppHelperUtil.showPopup(
              bctxAcrossAllLines.length === 0
                ? this.translate.instant('input_app_master_data_facade.noBctxAvailableToFetchMasterdataList.header')
                : this.translate.instant('input_app_master_data_facade.noDeptIdToFetch.header'),
              bctxAcrossAllLines.length === 0
                ? this.translate.instant('input_app_master_data_facade.noBctxAvailableToFetchMasterdataList.message')
                : this.translate.instant('input_app_master_data_facade.noDeptIdToFetch.message'),
            );
        },
      );
  }

  loadAllMasterdataLists(
    chainWithLoadSilhouetteInfo: boolean,
    masterDataListMetaData: TMasterDataListMetaData = { source: 'UNKNOWN' },
  ) {
    this.inputAppClientDBFacade
      .getAllBctxItemsAcrossAllLinesAndMasterdataListConfig()
      .pipe(take(1))
      .subscribe(
        ({
          bctxAcrossAllLines,
          silhouetteConf,
          masterdataList,
          listConf,
          subjectKey,
          useOperationSilhouetteMapping,
          operationsAndIssuesDepartmentId,
          operationsAndIssuesAllDepartmentIds,
          useOperationIssueMappingOnly,
        }) => {
          if (useOperationSilhouetteMapping || useOperationIssueMappingOnly) {
            if (bctxAcrossAllLines.length > 0) {
              if (operationsAndIssuesDepartmentId) {
                const allOperationIssueDepartments = MasterdataUtil.getAllOperationIssueDepartments(
                  operationsAndIssuesDepartmentId,
                  operationsAndIssuesAllDepartmentIds,
                );

                this.store.dispatch(
                  new LoadOperationsAndIssuesV2(
                    true,
                    chainWithLoadSilhouetteInfo,
                    bctxAcrossAllLines,
                    subjectKey,
                    masterdataList,
                    allOperationIssueDepartments,
                    listConf,
                    silhouetteConf,
                    masterDataListMetaData,
                  ),
                );
              } else {
                this.inputAppHelperUtil.showPopup(
                  this.translate.instant('input_app_master_data_facade.noDeptIdToFetch.header'),
                  this.translate.instant('input_app_master_data_facade.noDeptIdToFetch.message'),
                );
              }
            } else
              this.inputAppHelperUtil.showPopup(
                this.translate.instant('input_app_master_data_facade.noBctxAvailableToFetchMasterdataList.header'),
                this.translate.instant('input_app_master_data_facade.noBctxAvailableToFetchMasterdataList.message'),
              );
          } else {
            if (masterdataList.length > 0) {
              this.store.dispatch(
                new LoadAllMasterdataLists(
                  chainWithLoadSilhouetteInfo,
                  bctxAcrossAllLines,
                  masterdataList,
                  subjectKey,
                  listConf,
                  silhouetteConf,
                  masterDataListMetaData,
                ),
              );
            } else {
              this.inputAppHelperUtil.showPopup(
                this.translate.instant('input_app_master_data_facade.masterdataListNotConfigured.header'),
                this.translate.instant('input_app_master_data_facade.masterdataListNotConfigured.message'),
              );
            }
          }
        },
      );
  }

  updateMasterdataList(list: TAppSyncScreenItem) {
    this.inputAppClientDBFacade
      .getAllBctxItemsAcrossAllLinesAndMasterdataListConfig()
      .pipe(take(1))
      .subscribe(({ bctxAcrossAllLines, listConf, subjectKey }) => {
        if (bctxAcrossAllLines.length > 0) {
          this.store.dispatch(new AddOrUpdateMasterdataListByName(bctxAcrossAllLines, list, subjectKey, listConf));
        } else
          this.inputAppHelperUtil.showPopup(
            this.translate.instant('error_modal.Error'),
            this.translate.instant('input_app_master_data_facade.noBctxAvailableToUpdate'),
          );
      });
  }

  getAllMasterdataListsInfoAndBctxOrderKeys() {
    return this.store.pipe(select(getAllMasterdataListsInfoAndBctxOrderKeysAndConf));
  }

  selectMasterdataListStore() {
    return this.store.pipe(select(selectMasterdataListStore));
  }

  updateFilteredLists(id: string, bctx: TBctxItemWithoutSize[], filteredList: string[]) {
    this.store.dispatch(new UpdateFilteredLists(id, bctx, filteredList));
  }

  updateFilteredListsV3(bctx: TBctxItemWithoutSize[], updatedFilteredListData: TUpdateFilteredListsV3) {
    this.store.dispatch(new UpdateFilteredListsV3(bctx, updatedFilteredListData));
  }

  getUpdatedDBWithModifiedFilteredLists(id: string, bctx: TBctxItemWithoutSize[], filteredList: string[]) {
    return combineLatest([
      this.appMasterFacade.getAppSyncItemList(),
      this.getAllMasterdataListsInfoAndBctxOrderKeys(),
      this.selectMasterdataListStore(),
    ]).pipe(
      take(1),
      map(([getAppSyncItemList, [res, order, listConf], currentDB]) => {
        const listName = BCTXComputationHelper.getMasterDataListName(getAppSyncItemList, id);
        const req_id = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          order,
          MasterdataUtil.getFilteredBctx(bctx, listConf),
        );
        const details = res[req_id];
        const defaultDetails = res['default'];
        if (
          details &&
          details[listName] &&
          details[listName].masterdataList &&
          details[listName].masterdataList.list &&
          details[listName].masterdataList.list.length
        ) {
          return produce(currentDB, (draft) => {
            draft.allMasterdataListsByBctx[req_id][listName].filteredMasterdataList = filteredList;
          });
        } else if (defaultDetails && defaultDetails[listName]) {
          return produce(currentDB, (draft) => {
            draft.allMasterdataListsByBctx['default'][listName].filteredMasterdataList = filteredList;
          });
        } else {
          return currentDB;
        }
      }),
    );
  }

  getRejectsListV2(department: string): Observable<TMasterdataRejectList> {
    return this.selectMasterdataListStore().pipe(
      take(1),
      map((store) => {
        if (store.masterdataListV2[department]?.genericIssues.list) {
          return {
            status: 'SUCCESS',
            allList: store.masterdataListV2[department].genericIssues.list.filter(
              (issue) => issue.issueListTypes.REJECT_LIST,
            ),
            list:
              store.masterdataListV2[department].genericIssues.filteredRejectList.length === 0
                ? store.masterdataListV2[department].genericIssues.list.filter(
                    (issue) => issue.issueListTypes.REJECT_LIST,
                  )
                : store.masterdataListV2[department].genericIssues.list
                    .filter((issue) => issue.issueListTypes.REJECT_LIST)
                    .filter((issue) =>
                      store.masterdataListV2[department].genericIssues.filteredRejectList.find(
                        (id) => id === issue.issueName,
                      ),
                    ),
          };
        } else {
          return {
            status: 'FAILURE',
            error: 'NOT_FETCHED',
          };
        }
      }),
    );
  }

  getUpdatedDBWithModifiedFilteredRejectListV2(filteredList: string[], department: string) {
    return this.selectMasterdataListStore().pipe(
      take(1),
      map((store) => {
        return produce(store, (draft) => {
          if (draft.masterdataListV2[department]) {
            draft.masterdataListV2[department].genericIssues.filteredRejectList = filteredList;
          }
        });
      }),
    );
  }

  getUpdatedDBWithModifiedFilteredListsV3(
    bctx: TBctxItemWithoutSize[],
    updatedFilteredListData: TUpdateFilteredListsV3,
  ) {
    return this.selectMasterdataListStore().pipe(
      take(1),
      map((store) => {
        return produce(store, (draft) => {
          const productType = BCTXComputationHelper.getValueByKeyInBctxItems(bctx, 'product_type');
          const style = BCTXComputationHelper.getValueByKeyInBctxItems(bctx, 'style');
          if (updatedFilteredListData.type === 'FILTERED_OPERATIONS_LIST') {
            if (productType && style) {
              draft.masterdataListV2[updatedFilteredListData.department].listByProductTypeAndStyle[productType][
                style
              ].operations.filteredOperationsList = updatedFilteredListData.list;
            }
          } else if (updatedFilteredListData.type === 'FILTERED_GENERIC_ISSUE_LIST') {
            draft.masterdataListV2[updatedFilteredListData.department].genericIssues.filteredDefectList =
              updatedFilteredListData.list;
          } else if (updatedFilteredListData.type === 'FILTERED_MAPPED_ISSUE_LIST' && productType && style) {
            const operationUpdateData = draft.masterdataListV2[
              updatedFilteredListData.department
            ].listByProductTypeAndStyle[productType][style].operations.list.find(
              (operation) => operation.id === updatedFilteredListData.operation_id,
            );
            if (operationUpdateData) {
              operationUpdateData.filteredIssuesList = updatedFilteredListData.list;
            }
          }
        });
      }),
    );
  }

  updateFilteredRejectListV2(filteredList: string[], department: string) {
    this.store.dispatch(new UpdateFilteredRejectList(filteredList, department));
  }

  getOperationAndIssueAndSilhouetteImages(
    productType: string,
    style: string,
    department: string,
    operationsAndIssuesDepartmentId: string, // this is the current actual department in the QC application
  ): Observable<TOperationAnsIssueAndSilhouetteImagesResult> {
    return this.selectMasterdataListStore().pipe(
      take(1),
      map((store) => {
        if (
          store.masterdataListV2[department] &&
          store.masterdataListV2[department].listByProductTypeAndStyle[productType] &&
          store.masterdataListV2[department].listByProductTypeAndStyle[productType][style]
        ) {
          const lists = store.masterdataListV2[department].listByProductTypeAndStyle[productType][
            style
          ] as TOperationsAndIssuesAndSilhouetteOperations;
          if (
            lists.operations.list.length &&
            (lists.silhouetteOperations.backImageOperations.length ||
              lists.silhouetteOperations.frontImageOperations.length)
          ) {
            const operationIdToIssuesMap: Record<string, { issuesCount: number }> = {};
            lists.operations.list.forEach((item) => {
              operationIdToIssuesMap[item.id] = { issuesCount: item.issues.length };
            });

            const genericIssues = store.masterdataListV2[department].genericIssues.list.filter(
              (issue) => issue.issueListTypes.DEFECT_LIST,
            );

            //Checking whether all silhouette mapped operations exist in the source operations, as users can sync them independently
            const filteredBackImageOperations: TSilhouetteOperationOutput[] = [];
            const filteredFrontImageOperations: TSilhouetteOperationOutput[] = [];
            const hasIssues = (id: string) =>
              operationIdToIssuesMap[id] && (genericIssues.length || operationIdToIssuesMap[id].issuesCount);
            lists.silhouetteOperations.backImageOperations.forEach((item) => {
              const operationIds = item.operationIds.filter(hasIssues);
              if (operationIds.length) {
                filteredBackImageOperations.push({
                  ...item,
                  operationIds,
                  ...(item.operations && { operations: item.operations.filter((op) => hasIssues(op.id)) }),
                });
              }
            });
            lists.silhouetteOperations.frontImageOperations.forEach((item) => {
              const operationIds = item.operationIds.filter(hasIssues);
              if (operationIds.length) {
                filteredFrontImageOperations.push({
                  ...item,
                  operationIds,
                  ...(item.operations && { operations: item.operations.filter((op) => hasIssues(op.id)) }),
                });
              }
            });
            if (!filteredBackImageOperations.length && !filteredFrontImageOperations.length) {
              return {
                status: 'FAILURE',
                error: 'No issues present for all the mapped operations for silhouette images',
              };
            } else {
              return {
                status: 'SUCCESS',
                genericIssues,
                operations: lists.operations.list,
                silhouetteOperations: {
                  frontImageOperations: filteredFrontImageOperations,
                  backImageOperations: filteredBackImageOperations,
                },
              };
            }
          } else {
            if (operationsAndIssuesDepartmentId !== department) {
              return {
                status: 'SUCCESS',
                operations: [],
                genericIssues: [],
                silhouetteOperations: {
                  backImageOperations: [],
                  frontImageOperations: [],
                },
              };
            }
            let message = 'Please map silhouette operations';
            if (!lists.operations.list.length) {
              message = `Operations issues are not mapped for this Product-type: ${productType} and Style: ${style}`;
            }
            return {
              status: 'FAILURE',
              error: message,
            };
          }
        } else {
          return {
            status: 'FAILURE',
            error: `Operations issues are not mapped for this Product-type: ${productType} and Style: ${style}`,
          };
        }
      }),
    );
  }

  getOperationAndIssueForDepartments(
    productType: string,
    style: string,
    departments: string[],
  ): Observable<TDepartmentWiseOperationAndGenericIssueResult> {
    return this.selectMasterdataListStore().pipe(
      take(1),
      map((store) => {
        try {
          const deptWiseOperationIssue: TDepartmentWiseOperationAndGenericIssueResult = { status: 'SUCCESS', data: {} };
          departments.forEach((department) => {
            if (
              store.masterdataListV2[department] &&
              store.masterdataListV2[department].listByProductTypeAndStyle[productType] &&
              store.masterdataListV2[department].listByProductTypeAndStyle[productType][style]
            ) {
              const lists = store.masterdataListV2[department].listByProductTypeAndStyle[productType][style];
              const operationIdToIssuesMap: Record<string, { issuesCount: number }> = {};
              lists.operations.list.forEach((item) => {
                operationIdToIssuesMap[item.id] = { issuesCount: item.issues.length };
              });

              const genericIssues = store.masterdataListV2[department].genericIssues.list.filter(
                (issue) => issue.issueListTypes.DEFECT_LIST,
              );

              const filteredOperationList = lists.operations.filteredOperationsList.length
                ? lists.operations.list.filter((operation) =>
                    lists.operations.filteredOperationsList.includes(operation.name),
                  )
                : lists.operations.list;

              deptWiseOperationIssue.data[department] = {
                allOperationList: lists.operations.list,
                operationsList: filteredOperationList,
                genericIssues: {
                  list: genericIssues,
                  filteredDefectList: store.masterdataListV2[department].genericIssues.filteredDefectList,
                },
              };
            }
          });

          return deptWiseOperationIssue;
        } catch (error) {
          return {
            status: 'FAILURE',
            error: `Operations issues are not mapped for this Product-type: ${productType} and Style: ${style}`,
          };
        }
      }),
    );
  }

  getMasterdataList(id: string, bctx: TBctxItemWithoutSize[]): Observable<TMasterdataListResult> {
    return combineLatest([
      this.appMasterFacade.getAppSyncItemList(),
      this.getAllMasterdataListsInfoAndBctxOrderKeys(),
    ]).pipe(
      take(1),
      map(([getAppSyncItemList, [res, order, listConf]]) => {
        const listName = BCTXComputationHelper.getMasterDataListName(getAppSyncItemList, id);
        const req_id = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          order,
          MasterdataUtil.getFilteredBctx(bctx, listConf),
        );
        return BCTXComputationHelper.getMasterDataListValue(listName, req_id, res);
      }),
    );
  }

  getSilhouetteKeysInfoAndBctxOrderKeysAndConf() {
    return this.store.pipe(select(getSilhouetteKeysInfoAndBctxOrderKeysAndConf));
  }

  getSilhoutteKey(bctx: TBctxItemWithoutSize[]): Observable<TSilhouetteKeyResult> {
    return this.getSilhouetteKeysInfoAndBctxOrderKeysAndConf().pipe(
      take(1),
      map(([res, order, silhouetteConf]) => {
        const req_id = BCTXComputationHelper.getUniqueBctxKeyWithOrder(
          order,
          MasterdataUtil.getFilteredBctx(bctx, silhouetteConf),
        );
        if (res.keys[req_id]) {
          return {
            status: 'SUCCESS',
            silhouetteKey: res.keys[req_id],
          };
        } else
          return {
            status: 'FAILURE',
            error: 'NOT_CONFIGURED',
            message: 'Please map and sync the silhouette image and try again',
          };
      }),
    );
  }

  getDownloadSilhouetteAssetsPayload() {
    return this.store.pipe(select(getDownloadSilhouetteAssetsPayload));
  }

  getBctxItemsOrderWithoutSize() {
    return this.store.pipe(select(getBctxItemsOrderWithoutSize));
  }

  getUniqueProductTypeAndStyles(bctxList: TBctxItemWithoutSize[][]) {
    const set = new Set<string>();
    return bctxList.reduce((acc, curr) => {
      const productTypeOrProductCategory = BCTXComputationHelper.getProductTypeOrProductCategoryFromBctxItems(curr);
      const style = BCTXComputationHelper.getValueByKeyInBctxItems(curr, 'style');
      if (productTypeOrProductCategory && style) {
        const id = productTypeOrProductCategory + '_' + style;
        if (!set.has(id)) {
          set.add(id);
          acc.push({
            style,
            productType: productTypeOrProductCategory,
          });
        }
      }
      return acc;
    }, [] as TREQUEST_masterdataListsV2['productTypeStyles']);
  }

  downloadSilhouetteAssets() {
    this.store.dispatch(new DownloadSilhouetteAssets());
  }

  getLoadCorrectiveReasonsPayload() {
    return this.store.pipe(select(getLoadCorrectiveReasonsPayload));
  }

  loadCorrectiveReasons(
    listKey: string,
    chainWithLoadAllMasterdataListsAndSilhouetteKeys: boolean,
    masterDataListMetaData: TMasterDataListMetaData = { source: 'UNKNOWN' },
  ) {
    return this.store.dispatch(
      new LoadCorrectiveReasons(listKey, chainWithLoadAllMasterdataListsAndSilhouetteKeys, masterDataListMetaData),
    );
  }

  getCorrectiveReasonList() {
    return this.store.pipe(select(getCorrectiveReasonList));
  }

  isCorrectiveReasonsAvailable() {
    return this.store.pipe(select(isCorrectiveReasonsAvailable));
  }

  getAllMasterdataListsInfo() {
    return this.store.pipe(select(getAllMasterdataListsInfo));
  }
}
